/**
 * PIN vault service — Settings / LockPage から呼ばれる高レベル API。
 *
 * 役割:
 *   - **setupPin(pin)**: 現セッションの SK を PIN で wrap して IndexedDB に保存。
 *     UNLOCKED 状態必須。
 *   - **unlockWithPin(pin)**: 保存済 vault から SK を復元する。失敗回数を
 *     インクリメントし、`MAX_PIN_ATTEMPTS` 回失敗で vault を破棄してマスター
 *     パスワード unlock に強制 fallback (ookey-browser parity)。
 *   - **removePin()**: vault を削除 (ユーザーが Settings から「PIN を削除」)。
 *   - **getStatus()**: 設定済 / 残り試行回数 を返す (UI 表示用)。
 *
 * セキュリティ:
 *   - **zero-knowledge は維持**: PIN vault はローカル端末のみ。サーバには送らない。
 *   - **ユーザー結合**: vault 内に `userEmail` を保存し、読出時に現セッションの
 *     `material.email` と照合する。不一致 (別アカウント残骸) なら vault を破棄して
 *     'not-configured' を返す (TASK-WEB-PIN review I-1 multi-account leak 防御)。
 *   - **試行回数の cross-tab 整合性**: `navigator.locks.request('ookey-pin-vault', ...)`
 *     で exclusive lock を取り、read→PBKDF2→write を直列化する (review I-5 TOCTOU)。
 *   - **失敗時の SK エラー**: aes-gcm OperationError は内部でラップして
 *     PinUnlockError として throw する (DevTools logs に "OperationError" を残さない)。
 *   - **SK zeroize on failure**: 復号成功後に sessionStore に渡せなかった SK は
 *     呼出側 (LockPage) で zeroize する (review I-7)。本サービスは「成功 → SK 返却」
 *     のシンプル契約に留める。
 */

import { openSkWithPin, sealSkWithPin, type StoredPinVault } from '@/crypto/pin-vault'
import { zeroize } from '@/crypto/zeroize'
import { clearPinVault, readPinVault, writePinVault } from './pinVaultStore'
import { sessionStore } from './sessionStore'

/** cross-tab 並列 unlock の TOCTOU 防御。navigator.locks 非対応環境 (古い browser /
 * SSR / 一部 webview) では fallback して関数を直接実行する (single-tab 動作)。 */
const PIN_LOCK_NAME = 'ookey-pin-vault'

const withPinLock = async <T>(fn: () => Promise<T>): Promise<T> => {
  if (typeof navigator !== 'undefined' && 'locks' in navigator && navigator.locks) {
    return await navigator.locks.request(PIN_LOCK_NAME, { mode: 'exclusive' }, () => fn())
  }

  return fn()
}

/** 連続失敗の許容回数。これ以上で vault 破棄 (ookey-browser parity)。 */
export const MAX_PIN_ATTEMPTS = 5

export type PinStatus =
  | { readonly configured: false }
  | {
      readonly configured: true
      readonly attemptsLeft: number
      /** 設定時の PIN 桁数 (4-6)。LockPage がこの数だけ入力 box を出す。 */
      readonly pinLength: number
    }

export type PinUnlockErrorReason =
  | 'not-configured' // vault が無い (UI バグ抑止用)
  | 'wrong-pin' // PIN 違いまたは vault 改ざん。attemptsLeft が付く
  | 'locked-out' // 失敗回数オーバー、vault は既に破棄済
  | 'no-session' // LOCKED でも UNLOCKED でもないので unlock 不能

export class PinUnlockError extends Error {
  readonly reason: PinUnlockErrorReason
  readonly attemptsLeft?: number

  constructor(reason: PinUnlockErrorReason, attemptsLeft?: number) {
    super(`pin unlock failed: ${reason}`)
    this.name = 'PinUnlockError'
    this.reason = reason
    this.attemptsLeft = attemptsLeft
  }
}

export class PinSetupError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'PinSetupError'
  }
}

/**
 * 現セッションの SK を PIN で wrap して保存する。
 *
 * UNLOCKED 必須 (SK が無いと wrap できない)。既に PIN が設定されている場合は
 * 上書き (ユーザーが「PIN を変更」した想定)。
 */
export const setupPin = async (pin: string): Promise<void> =>
  withPinLock(async () => {
    const state = sessionStore.getState()

    if (state.kind !== 'unlocked') {
      throw new PinSetupError('cannot setup PIN: session is not unlocked')
    }
    if (state.material.email === '') {
      throw new PinSetupError('cannot setup PIN: session has no user email')
    }

    const vault = await sealSkWithPin(pin, state.symmetricKey, state.material.email)

    await writePinVault(vault)
  })

/**
 * PIN から SK を復元する。成功時は SK を返す (sessionStore.unlock は呼出側で行う)。
 *
 * 失敗時の挙動:
 *   - vault 無し → `PinUnlockError('not-configured')`
 *   - 復号失敗 → failedAttempts++ を write して `PinUnlockError('wrong-pin', N)`
 *   - failedAttempts >= MAX_PIN_ATTEMPTS で次回呼出時に vault clear して
 *     `PinUnlockError('locked-out')` (この後はマスターパスワード必須)
 */
export const unlockWithPin = async (pin: string): Promise<Uint8Array> =>
  withPinLock(async () => {
    const state = sessionStore.getState()

    if (state.kind === 'unauth') {
      throw new PinUnlockError('no-session')
    }

    const vault = await readPinVault()

    if (vault === null) {
      throw new PinUnlockError('not-configured')
    }

    // ユーザー結合チェック: 別アカウントで設定された vault の残骸なら破棄。
    // (logout で clearPinVault される設計だが、Sidenav 経由でない強制 close /
    // 別ブラウザプロファイル復元 等を考慮した defense in depth)
    const currentEmail = state.material.email.toLowerCase()

    if (vault.userEmail !== currentEmail) {
      await clearPinVault()
      throw new PinUnlockError('not-configured')
    }

    if (vault.failedAttempts >= MAX_PIN_ATTEMPTS) {
      await clearPinVault()
      throw new PinUnlockError('locked-out')
    }

    let sk: Uint8Array | null = null

    try {
      sk = await openSkWithPin(pin, vault)

      // 成功 → 失敗カウンタを 0 にリセット。リセット書込が IDB エラーで失敗しても
      // SK 復元自体は成功しているので unlock は通す (次回読出時に古い counter が
      // 残るが、5 未満なら無害)。
      if (vault.failedAttempts > 0) {
        try {
          await writePinVault({ ...vault, failedAttempts: 0 })
        } catch {
          // IDB write fail はカウンタリセットを諦め、unlock は成功扱い
        }
      }

      return sk
    } catch (err) {
      // 復号成功後に IDB reset 等で throw した場合の SK leak を防ぐ
      // (catch (err) ブロックに到達 = sk は呼出側に渡らない)
      if (sk !== null) {
        zeroize(sk)
        // sk 復元成功 + 書込失敗 = PIN は正しいが永続化に失敗。
        // 失敗カウンタ更新もしないため、呼出側には 'wrong-pin' ではなく
        // 純粋な「IDB 不能」を伝えたい → 一旦 wrong-pin として扱うが
        // カウンタは増やさない (上で zeroize 済、sk = null)。
        throw new PinUnlockError('wrong-pin', MAX_PIN_ATTEMPTS - vault.failedAttempts)
      }

      // PIN 違い以外 (IDB error / network 等) は失敗カウンタを増やさず再送出。
      // OperationError (= aes-gcm 認証タグ失敗) のみ wrong-pin として扱う。
      const isWrongPin = err instanceof Error && err.name === 'OperationError'

      if (!isWrongPin) {
        throw new PinUnlockError('wrong-pin', MAX_PIN_ATTEMPTS - vault.failedAttempts)
      }

      const nextAttempts = vault.failedAttempts + 1

      if (nextAttempts >= MAX_PIN_ATTEMPTS) {
        await clearPinVault()
        throw new PinUnlockError('locked-out')
      }

      await writePinVault({ ...vault, failedAttempts: nextAttempts })
      throw new PinUnlockError('wrong-pin', MAX_PIN_ATTEMPTS - nextAttempts)
    }
  })

/** ユーザーが Settings から PIN を削除した時に呼ぶ。
 *  Sidenav の logout からも呼ばれる (multi-account 切替対応、review I-1)。 */
export const removePin = async (): Promise<void> =>
  withPinLock(async () => {
    await clearPinVault()
  })

/** Settings + LockPage が「PIN 設定済か / 残り何回試行できるか」を表示するための snapshot。 */
export const getPinStatus = async (): Promise<PinStatus> => {
  const vault = await readPinVault()

  if (vault === null) return { configured: false }

  return {
    configured: true,
    attemptsLeft: Math.max(0, MAX_PIN_ATTEMPTS - vault.failedAttempts),
    pinLength: vault.pinLength,
  }
}

/** spec 用: 内部的に保存済 vault を覗く。本番コードから呼ばない。 */
export const __readVaultForTest = async (): Promise<StoredPinVault | null> => readPinVault()
